// ─────────────────────────────────────────────────────────────────────────────
// utils/anspruch.ts — DER ANSPRUCH KOMMT AUS DEM CUSTOM CLAIM, NICHT AUS EINEM
// FIRESTORE-FELD (T-M30.7 — REQ-SEC-028, REQ-SEC-027, REQ-DM-070, REQ-OPS-052)
//
// ── WOFUER ──────────────────────────────────────────────────────────────────
// Bis zum 2026-09-01 las die Oberflaeche die Rolle aus `users/{uid}.role` —
// aus demselben Dokument, dessen Lesbarkeit sie damit voraussetzte. Mit
// REQ-SEC-028 stellt die Cloud den Anspruch als CUSTOM CLAIM aus
// (`onMemberChanged`, `onUserChanged`, spaeter `acceptinvitation`), und die
// Rules pruefen genau diesen Claim (REQ-SEC-029). **Die Oberflaeche liest
// deshalb dieselbe Tatsache an derselben Stelle wie die Rules** — ein zweiter
// Leseweg auf dieselbe Tatsache waere die Doppelfuehrung, die REQ-SEC-031 (a)
// ausdruecklich vermeidet.
//
// ── DIE ZWEI NAMEN SIND FEST (REQ-OV-008 Praez. 1 (c)) ─────────────────────
// `admin` (Wahrheitswert) und `companyIds` (Liste). Sie stehen hier als
// Konstanten, weil sie an drei Orten dasselbe bedeuten muessen: die Cloud
// stellt sie aus (`firebase/functions/src/claims.ts`), die Rules lesen sie,
// und diese Datei ist der dritte Leser — kein vierter Name.
//
// ⚠ **`companyId` IN DER EINZAHL IST EIN ANDERER ANSPRUCH** (REQ-SC-052): ihn
// traegt das ANZEIGE-Token eines Bildschirm-Kanals. Wer ihn hier laese, gaebe
// jedem Bildschirm den vollen Mandanten — dieselbe Falle, die REQ-SEC-029 (d)
// in den Rules benennt. Diese Datei liest ihn nirgends.
//
// ── DIE STRENGE RICHTUNG (REQ-SEC-028 (a)) ─────────────────────────────────
// **Nur `admin === true` bedeutet Admin**; jeder andere Wert und ein fehlender
// Claim bedeuten „kein Admin". **Eine fehlende oder leere `companyIds`
// bedeutet KEIN Mandant — nie „alle".** Ein nicht lesbarer Anspruch ist damit
// der engste und nicht der weiteste; das ist dieselbe Richtung wie
// REQ-DM-070 (a).
//
// ── DIE AUFFRISCHUNG IST PFLICHT (REQ-SEC-028 (f)) ─────────────────────────
// Ein ausgestelltes ID-Token traegt den alten Anspruch **bis zu einer Stunde**
// weiter. Ohne ausdrueckliches Auffrischen saehe ein eben aufgenommenes
// Mitglied so lange eine Fehlermeldung statt seiner Daten — und der Betrieb
// suchte den Fehler in den Rules. Deshalb:
//   • beim Anmelden wird der Anspruch ERZWUNGEN gelesen (`leseAnspruch(user,
//     { auffrischen: true })`, gerufen im Anmelde-Start des `authStore`),
//   • nach einem `permission-denied` wird EINMAL aufgefrischt und der Vorgang
//     EINMAL wiederholt, BEVOR der Fehler angezeigt wird (`liveAbo` fuer die
//     Abos, {@link mitAnspruchsWiederholung} fuer die einmaligen Lesevorgaenge).
// **Scheitert der Wiederholversuch, ist es ein echter Rechtefehler** und wird
// als solcher gemeldet (REQ-OPS-052).
//
// ── GRENZEN, benannt statt verschwiegen ────────────────────────────────────
//  (i)   **Keine dieser Funktionen wirft.** Sie laufen in Start- und
//        Fehlerpfaden; eine Auskunft, die ihren Aufrufer faellt, waere
//        schlimmer als keine. Ein Fehlschlag ist {@link KEIN_ANSPRUCH} bzw.
//        `false`.
//  (ii)  **Der Anspruch ist keine Durchsetzung.** Er entscheidet, was die
//        Oberflaeche ZEIGT und ABFRAGT; durchgesetzt wird in den Rules
//        (REQ-SEC-029, T-M30.3). Wer hier etwas verbirgt, hat nichts gesperrt.
//  (iii) **Ein Anzeige-Token traegt diesen Anspruch nie** — es hat weder
//        `admin` noch `companyIds`. Es faellt damit auf {@link KEIN_ANSPRUCH}
//        und wird am Portal ohnehin abgemeldet (`utils/bildschirmSitzung.ts`).
// ─────────────────────────────────────────────────────────────────────────────

// `auth` ist hier seit dem 2026-09-08 nur noch der VORGABEWERT der uebergebenen
// Anmeldung (T-M34.2, REQ-SC-109 (3)) — die Portal-Instanz `[DEFAULT]`. Wer
// eine andere braucht, uebergibt sie; gebildet wird sie hier nicht.
import { auth } from '@/firebase/config';
import { diagnoseAuthState } from '@/utils/diagnose';

/** Der Anspruchs-Claim „Administrator" — fester Name (REQ-OV-008 Praez. 1 (c)). */
export const ANSPRUCH_ADMIN = 'admin';

/** Der Anspruchs-Claim „Mandanten" — fester Name, MEHRZAHL (REQ-SEC-028 (a)). */
export const ANSPRUCH_MANDANTEN = 'companyIds';

/**
 * Der Fehlercode, der einen Rechtefehler traegt (REQ-SEC-028 (f)).
 *
 * `unauthenticated` gehoert ausdruecklich NICHT dazu: dort hilft kein frisches
 * Token derselben Sitzung, sondern nur eine neue Anmeldung.
 */
export const RECHTEFEHLER_CODE = 'permission-denied';

/** Der gelesene Anspruch — genau die zwei Angaben, die die Rules pruefen. */
export interface Anspruch {
  /** `true` nur bei `admin === true` im Claim (REQ-SEC-028 (a)). */
  istAdmin: boolean;
  /** Die Mandanten des Benutzers; leer bedeutet KEIN Mandant, nie „alle". */
  companyIds: string[];
}

/** Der Anspruch in jedem Zweifelsfall: der engste (REQ-SEC-028 (a)). */
export const KEIN_ANSPRUCH: Anspruch = { istAdmin: false, companyIds: [] };

/**
 * Die Sicht auf einen angemeldeten Nutzer, so weit dieses Modul sie braucht.
 *
 * Bewusst ein eigener, MINIMALER Typ und nicht `User` — dieselbe Bauform wie in
 * `utils/bildschirmSitzung.ts`: ein echter Firebase-`User` erfuellt ihn
 * strukturell, eine Attrappe im Test ebenso. **Beide Methoden sind optional**,
 * und das haelt Grenze (i) im Typ fest statt in einem Kommentar.
 */
export interface AnspruchsSitzung {
  readonly uid?: string;
  getIdTokenResult?: (forceRefresh?: boolean) => Promise<{
    readonly claims?: Record<string, unknown>;
  } | null | undefined>;
  getIdToken?: (forceRefresh?: boolean) => Promise<string>;
}

/**
 * DIE ANMELDUNG WIRD UEBERGEBEN, NICHT GEBILDET (2026-09-08, T-M34.2 —
 * REQ-SC-109 (3)).
 *
 * Bis zum 2026-09-08 las dieses Modul die Anmeldung FEST aus
 * `@/firebase/config` (`auth`). Seit der Trennung von Verwaltungs- und
 * Bildschirm-Sitzung gibt es zwei davon, und wer die falsche fragte, fraegte
 * eine leere. Die Instanz kommt deshalb ab jetzt vom Aufrufer.
 *
 * **Der Vorgabewert ist die PORTAL-Anmeldung** — jeder heutige Aufrufer dieses
 * Moduls gehoert zur Verwaltungsoberflaeche und bleibt damit unveraendert; die
 * Bildschirme uebergeben ausdruecklich `screenAuth` (heute allein ueber
 * `utils/liveAbo.ts`, siehe dort).
 *
 * Bewusst ein MINIMALER Typ und nicht `Auth`: eine echte Firebase-Anmeldung
 * erfuellt ihn strukturell, eine Attrappe im Test ebenso — dieselbe Bauform wie
 * {@link AnspruchsSitzung} darueber.
 */
export interface AnmeldeQuelle {
  readonly currentUser: AnspruchsSitzung | null;
}

/** Eine Liste von Kennungen aus einem Claim — Fremdwerte fallen weg. */
function leseKennungen(wert: unknown): string[] {
  if (!Array.isArray(wert)) return [];
  return wert.filter((k): k is string => typeof k === 'string' && k.length > 0);
}

/** Der Anspruch aus einer Claim-Menge (die reine Rechnung, ohne Netz). */
export function anspruchAusClaims(claims: Record<string, unknown> | null | undefined): Anspruch {
  if (!claims || typeof claims !== 'object') return { ...KEIN_ANSPRUCH };
  return {
    // STRIKT `=== true`, wie die Rules (`== true`) und nicht „wahrheitswertig":
    // ein `admin: 'false'` waere sonst ein Administrator.
    istAdmin:   claims[ANSPRUCH_ADMIN] === true,
    companyIds: leseKennungen(claims[ANSPRUCH_MANDANTEN]),
  };
}

/**
 * Liest den Anspruch aus dem ID-Token (REQ-SEC-028 (a)).
 *
 * `auffrischen: true` erzwingt ein frisches Token (`getIdTokenResult(true)`) —
 * das ist die Pflicht aus (f) beim Anmelden. **Scheitert die Auffrischung, wird
 * der zwischengespeicherte Anspruch gelesen** statt den Benutzer auszusperren:
 * ein Netzfehler beim Auffrischen ist kein Entzug von Rechten.
 *
 * **Sie wirft nie** (Grenze (i)).
 */
export async function leseAnspruch(
  user: AnspruchsSitzung | null | undefined,
  optionen: { auffrischen?: boolean } = {},
): Promise<Anspruch> {
  if (!user || typeof user.getIdTokenResult !== 'function') return { ...KEIN_ANSPRUCH };
  if (optionen.auffrischen) {
    try {
      const frisch = await user.getIdTokenResult(true);
      return anspruchAusClaims(frisch?.claims);
    } catch {
      // Weiter zum zwischengespeicherten Ausweis — siehe oben.
      diagnoseAuthState('claimRefreshFailed', { userId: user.uid, source: 'bootstrap' }, 'warn');
    }
  }
  try {
    const ergebnis = await user.getIdTokenResult();
    return anspruchAusClaims(ergebnis?.claims);
  } catch {
    return { ...KEIN_ANSPRUCH };
  }
}

/**
 * DIE AUFFRISCHUNG NACH EINEM RECHTEFEHLER (REQ-SEC-028 (f)).
 *
 * Erzwingt ein frisches ID-Token der laufenden Sitzung. Gibt `true` zurueck,
 * wenn danach ein Wiederholversuch sinnvoll ist — `false`, wenn niemand
 * angemeldet ist oder das Auffrischen selbst scheitert; dann ist der Fehler zu
 * melden, statt einen Versuch zu wiederholen, der dieselbe Antwort bekaeme.
 *
 * **Gefragt wird `currentUser` der uebergebenen Anmeldung und NICHT der
 * `authStore`** — dieselbe Begruendung wie in `utils/liveAbo.ts`: dieses Modul
 * liegt UNTER den Stores und darf sie nicht voraussetzen.
 *
 * *Bis zum 2026-09-08 stand hier „Gefragt wird `auth.currentUser`" und das
 * Modul las die eine Anmeldung fest aus `@/firebase/config`; seit der Trennung
 * der zwei Instanzen (REQ-SC-109 (3)) wird sie UEBERGEBEN, mit der
 * Portal-Anmeldung als Vorgabe. Berichtigt statt geloescht, REQ-OPS-036
 * Praez. 6.*
 */
export async function frischeAnspruchAuf(
  quelle = 'unknown',
  anmeldung: AnmeldeQuelle = auth,
): Promise<boolean> {
  let nutzer: AnspruchsSitzung | null = null;
  try {
    nutzer = (anmeldung?.currentUser ?? null) as AnspruchsSitzung | null;
  } catch {
    return false;
  }
  if (!nutzer || typeof nutzer.getIdToken !== 'function') return false;
  try {
    await nutzer.getIdToken(true);
    diagnoseAuthState('claimRefreshed', { userId: nutzer.uid, source: quelle });
    return true;
  } catch (err) {
    diagnoseAuthState(
      'claimRefreshFailed',
      { userId: nutzer.uid, source: quelle, code: (err as { code?: string })?.code ?? 'unknown' },
      'warn',
    );
    return false;
  }
}

/** Traegt ein geworfener Wert den Rechtefehler-Code? */
export function istRechtefehler(err: unknown): boolean {
  return (err as { code?: unknown } | null)?.code === RECHTEFEHLER_CODE;
}

/**
 * EIN EINZELNER LESE-/SCHREIBVORGANG MIT DER PFLICHT AUS (f).
 *
 * Scheitert `aktion` mit `permission-denied`, wird der Anspruch aufgefrischt
 * und die Aktion **genau einmal** wiederholt. Scheitert sie erneut — oder
 * scheitert schon das Auffrischen —, wird der ursprungliche Fehler
 * WEITERGEWORFEN: es ist dann ein echter Rechtefehler und wird als solcher
 * gemeldet (REQ-OPS-052).
 *
 * ⚠ **EINMAL heisst einmal.** Eine Schleife ueber „solange abgewiesen" waere
 * bei einem echten Rechtefehler eine Endlosschleife gegen die Cloud.
 *
 * `anmeldung` wird nur durchgereicht (Vorgabe: die Portal-Anmeldung, T-M34.2 /
 * REQ-SC-109 (3)). **Die Bildschirme benutzen diesen Weg heute nicht** — an
 * ihnen bedeutet `permission-denied` „Kanal umgestellt oder Anspruch entzogen",
 * und der Rueckweg ist `screentoken` und kein frisches Token derselben Sitzung
 * (REQ-OPS-053 (f)). Der Parameter steht hier, damit die Instanz auch in diesem
 * Modul nirgends mehr fest verdrahtet ist.
 */
export async function mitAnspruchsWiederholung<T>(
  quelle: string,
  aktion: () => Promise<T>,
  anmeldung: AnmeldeQuelle = auth,
): Promise<T> {
  try {
    return await aktion();
  } catch (err) {
    if (!istRechtefehler(err)) throw err;
    const aufgefrischt = await frischeAnspruchAuf(quelle, anmeldung);
    if (!aufgefrischt) throw err;
    return await aktion();
  }
}
